/**
 * The label of every document type the app knows a translation for, keyed by the lower-cased
 * `labelKey` the API sends.
 *
 * One source for every surface that names one: the upload dialog's dropdowns and the document
 * tables read the same table.
 */
const DOKUMENT_TYP_LABELS: Readonly<Record<string, string>> = {
  offerte: $localize`:@@dokumente.dokumentTyp.offerte:Offerte`,
  vertrag: $localize`:@@dokumente.dokumentTyp.vertrag:Vertrag`,
  bericht: $localize`:@@dokumente.dokumentTyp.bericht:Bericht`,
  schulung: $localize`:@@dokumente.dokumentTyp.schulung:Schulung`,
  export: $localize`:@@dokumente.dokumentTyp.export:Export`,
  zusatzauftrag: $localize`:@@dokumente.dokumentTyp.zusatzauftrag:Zusatzauftrag`,
  'lc-audit': $localize`:@@dokumente.berichtSubTyp.lcAudit:LC-Audit`,
  initialanalyse: $localize`:@@dokumente.berichtSubTyp.initialanalyse:Initialanalyse`,
  begehung: $localize`:@@dokumente.berichtSubTyp.begehung:Begehung`,
};

/** The subtypes a «Bericht» can carry. The values are the API's `BerichtTypEnum` literals. */
export enum BerichtSubTyp {
  LcAudit = 'LC-Audit',
  Initialanalyse = 'Initialanalyse',
  Begehung = 'Begehung',
}

/** The label of every document subtype. */
export const BERICHT_SUB_TYP_LABELS: Readonly<Record<BerichtSubTyp, string>> = {
  [BerichtSubTyp.LcAudit]: $localize`:@@dokumente.berichtSubTyp.lcAudit:LC-Audit`,
  [BerichtSubTyp.Initialanalyse]: $localize`:@@dokumente.berichtSubTyp.initialanalyse:Initialanalyse`,
  [BerichtSubTyp.Begehung]: $localize`:@@dokumente.berichtSubTyp.begehung:Begehung`,
};

/**
 * Names a document type or subtype from the `labelKey` of its `UploadDokumentTyp`. The key is an
 * i18n key rather than display text, so a key without a translation here is shown as it is, with
 * its first letter capitalised.
 *
 * @param {string | null | undefined} labelKey - The key as the API sent it
 * @returns {string} The label, or an empty string when there is no key
 */
export function resolveDokumentTypLabel(labelKey: string | null | undefined): string {
  if (!labelKey) {
    return '';
  }
  return DOKUMENT_TYP_LABELS[labelKey.toLowerCase()] ?? labelKey.charAt(0).toUpperCase() + labelKey.slice(1);
}
